import React, { useCallback, useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUploadClaimCodes } from '~/hooks/apis/claimCampaigns';

import type { ClaimCampaign, UploadClaimCodesResponse } from '~/services/LuxLoyalty';

interface UploadClaimCodesDialogProps {
  open: boolean;
  campaign: ClaimCampaign | null;
  onClose: () => void;
}

interface FormData {
  codesText: string;
  expiresAt: string;
  maxRedemptions: string;
}

const DEFAULT_VALUES: FormData = {
  codesText: '',
  expiresAt: '',
  maxRedemptions: '',
};

const MAX_CODES = 10000;

export default function UploadClaimCodesDialog({ open, campaign, onClose }: UploadClaimCodesDialogProps) {
  const uploadMutation = useUploadClaimCodes();
  const [uploadResult, setUploadResult] = useState<UploadClaimCodesResponse['result'] | null>(null);

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const isSharedCode = campaign?.validatorType === 'SHARED_CODE';

  // Reset form and result when dialog opens/closes
  useEffect(() => {
    if (open) {
      reset(DEFAULT_VALUES);
      setUploadResult(null);
    }
  }, [open, reset]);

  /**
   * Parses the codes text area into an array of unique, non-empty codes.
   * Supports newline, comma, or semicolon delimiters.
   */
  const parseCodes = useCallback((text: string): string[] => {
    return [
      ...new Set(
        text
          .split(/[\n,;]+/)
          .map((c) => c.trim())
          .filter(Boolean),
      ),
    ];
  }, []);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!campaign) return;

      const codes = parseCodes(data.codesText);
      if (codes.length === 0) return;

      try {
        const response = await uploadMutation.mutateAsync({
          campaignId: campaign.id,
          body: {
            codes,
            expiresAt: data.expiresAt ? new Date(data.expiresAt).toISOString() : undefined,
            maxRedemptions: isSharedCode && data.maxRedemptions ? Number(data.maxRedemptions) : undefined,
          },
        });
        setUploadResult(response.result);
      } catch {
        // Error handled in mutation hook
      }
    },
    [campaign, uploadMutation, parseCodes, isSharedCode],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Codes — {campaign?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="codesText"
            control={control}
            rules={{
              required: 'At least one code is required',
              validate: (value) => {
                const count = parseCodes(value).length;
                if (count === 0) return 'At least one code is required';
                if (count > MAX_CODES) return `Maximum ${MAX_CODES} codes allowed`;
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Codes"
                placeholder="Enter codes, one per line or comma-separated"
                multiline
                rows={8}
                fullWidth
                required
                error={!!error}
                helperText={
                  error?.message || `Paste up to ${MAX_CODES} codes (one per line, comma, or semicolon separated)`
                }
              />
            )}
          />

          <Controller
            name="expiresAt"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Expiry Date (optional)"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />

          {/* maxRedemptions is only relevant for SHARED_CODE campaigns */}
          {isSharedCode && (
            <Controller
              name="maxRedemptions"
              control={control}
              rules={{
                required: 'Max redemptions is required for shared codes',
                validate: (v) => Number(v) >= 1 || 'Must be at least 1',
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Max Redemptions"
                  type="number"
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                  error={!!error}
                  helperText={error?.message || 'Maximum number of times each code can be redeemed'}
                />
              )}
            />
          )}

          {/* Upload result summary */}
          {uploadResult && (
            <Alert severity="success">
              <Typography variant="body2">
                <strong>Requested:</strong> {uploadResult.requestedCount}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {uploadResult.createdCount}
              </Typography>
              <Typography variant="body2">
                <strong>Duplicates skipped:</strong> {uploadResult.duplicatesSkipped}
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {uploadResult ? 'Close' : 'Cancel'}
        </Button>
        {!uploadResult && (
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={uploadMutation.isPending}>
            {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
